import * as React from 'react';

export interface UseControllableStateProps<T> {
  defaultValue?: T;
  onChange?(next: T): void;
  /** When set, acts controlled; otherwise `defaultValue` seeds internal state until updates. */
  value?: T;
}

/** Controlled / uncontrolled state with optional `onChange`. */
export function useControllableState<T>({
  defaultValue,
  onChange,
  value: valueProp,
}: UseControllableStateProps<T>): readonly [T, React.Dispatch<React.SetStateAction<T>>] {
  const isControlled = valueProp !== undefined;

  const [innerState, setInnerState] = React.useState<T>(() => defaultValue as T);

  const value = (isControlled ? valueProp : innerState) as T;

  const setValue = React.useCallback(
    (nextValue: React.SetStateAction<T>) => {
      const next = (
        typeof nextValue === 'function' ? (nextValue as (prev: T) => T)(value) : nextValue
      ) as T;
      if (!isControlled) {
        setInnerState(next);
      }
      onChange?.(next);
    },
    [isControlled, onChange, value],
  );

  return [value, setValue] as const;
}
